import type { PlatformCreditPolicy, PlatformPlan, PlatformTopup } from '@/types/platformBilling';

/** Catalog fields the billing copy is rendered from; every field is optional so public and partial catalogs work. */
export interface BillingCopyCatalog {
  currency?: string;
  plans?: PlatformPlan[];
  topups?: PlatformTopup[];
  creditPolicy?: PlatformCreditPolicy;
}

/** Catalog amounts are major units of the catalog currency (the field name predates multi-currency). */
export const formatBillingMoney = (amount: number, currency: string | null | undefined): string => {
  const code = (currency || 'USD').toUpperCase();
  const fractionDigits = Number.isInteger(amount) ? 0 : 2;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: code,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(amount);
  } catch {
    return `${code} ${amount.toFixed(fractionDigits)}`;
  }
};

// Last-resort values, used only when the catalog has not loaded or omits a field.
const FALLBACK_INCLUDED_CREDITS: Record<PlatformPlan['tier'], number> = { basic: 35, professional: 100 };
const FALLBACK_TOPUP_AMOUNTS = [9, 15, 28];

export const resolveCreditPolicy = (catalog: BillingCopyCatalog | null | undefined) => {
  const includedCredits = (tier: PlatformPlan['tier']) => (
    catalog?.plans?.find((plan) => plan.tier === tier)?.includedCredits ?? FALLBACK_INCLUDED_CREDITS[tier]
  );
  const rolloverPercent = catalog?.creditPolicy?.includedRolloverPercent ?? 20;
  const rolloverCredits = (tier: PlatformPlan['tier']) => Math.floor(includedCredits(tier) * rolloverPercent / 100);
  const topupAmounts = catalog?.topups?.length
    ? [...catalog.topups].map((topup) => topup.amountUsd).sort((left, right) => left - right)
    : FALLBACK_TOPUP_AMOUNTS;
  return {
    basicCredits: includedCredits('basic'),
    professionalCredits: includedCredits('professional'),
    rolloverPercent,
    basicRolloverCredits: rolloverCredits('basic'),
    professionalRolloverCredits: rolloverCredits('professional'),
    rolloverCycles: catalog?.creditPolicy?.includedRolloverCycles ?? 1,
    purchasedExpiryMonths: catalog?.creditPolicy?.purchasedExpiryMonths ?? 12,
    topupPrices: topupAmounts.map((amount) => formatBillingMoney(amount, catalog?.currency)),
  };
};
